import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './App.css';

// Markdownコードブロック（```markdown ... ```）を除去するヘルパー関数
const stripMarkdownCodeBlock = (text) => {
  if (!text) return text;

  // ```markdown で始まり ``` で終わる場合、その内側を取得
  const match = text.match(/^```markdown\s*\n([\s\S]*?)\n```\s*$/);
  if (match) {
    return match[1];
  }

  // ```で始まり```で終わる場合も対応（言語指定なし）
  const matchNoLang = text.match(/^```\s*\n([\s\S]*?)\n```\s*$/);
  if (matchNoLang) {
    return matchNoLang[1];
  }

  return text;
};

const sanitizeIntegratedSearchUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:'
      ? parsedUrl.href
      : '';
  } catch {
    return '';
  }
};

const integratedSearchMarkdownComponents = {
  img: () => null,
  a: ({ href, children }) => {
    const safeUrl = sanitizeIntegratedSearchUrl(href);
    return safeUrl ? (
      <a href={safeUrl} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ) : <span>{children}</span>;
  }
};

const getIntegratedSearchSourceLabel = (sources, source) => (
  source === 'agent'
    ? '統合検索エージェント'
    : sources.find((candidate) => candidate.value === source)?.label
    || source
);

const getIntegratedSearchToolCategory = (toolName) => {
  const normalizedName = toolName.toLowerCase();
  if (normalizedName.includes('sharepoint')) return 'SharePoint / M365';
  if (normalizedName.includes('teams')) return 'Teams';
  if (normalizedName.includes('tavily')) return '公開 Web / Tavily';
  if (normalizedName.includes('ocr')) return 'OCR';
  return 'その他';
};

const createIntegratedSearchArguments = (tool) => {
  const schema = tool?.input_schema;
  const properties = schema?.properties;
  const required = Array.isArray(schema?.required) ? schema.required : [];
  if (!properties || typeof properties !== 'object') return '{}';

  const argumentsObject = {};
  required.forEach((propertyName) => {
    const property = properties[propertyName] || {};
    if (property.default !== undefined) {
      argumentsObject[propertyName] = property.default;
    } else if (property.type === 'boolean') {
      argumentsObject[propertyName] = false;
    } else if (property.type === 'number' || property.type === 'integer') {
      argumentsObject[propertyName] = 0;
    } else if (property.type === 'array') {
      argumentsObject[propertyName] = [];
    } else if (property.type === 'object') {
      argumentsObject[propertyName] = {};
    } else {
      argumentsObject[propertyName] = '';
    }
  });
  return JSON.stringify(argumentsObject, null, 2);
};

const extractIntegratedSearchCollection = (responses, preferredKey) => {
  if (Array.isArray(responses)) {
    const directItems = responses.filter((item) => getIntegratedSearchItemId(item));
    if (directItems.length > 0) return directItems;

    for (const item of responses) {
      if (item && typeof item.text === 'string') {
        try {
          const parsed = JSON.parse(item.text);
          const extracted = extractIntegratedSearchCollection(parsed, preferredKey);
          if (extracted.length > 0) return extracted;
        } catch (error) {
          console.debug('[IntegratedSearch] MCP text is not JSON:', error);
        }
      }
      const extracted = extractIntegratedSearchCollection(item, preferredKey);
      if (extracted.length > 0) return extracted;
    }
    return [];
  }
  if (!responses || typeof responses !== 'object') return [];
  if (Array.isArray(responses[preferredKey])) return responses[preferredKey];

  for (const value of Object.values(responses)) {
    const extracted = extractIntegratedSearchCollection(value, preferredKey);
    if (extracted.length > 0) return extracted;
  }
  return [];
};

const getIntegratedSearchItemId = (item) => (
  item?.id || item?.team_id || item?.teamId || item?.channel_id || item?.channelId || ''
);

const getIntegratedSearchItemLabel = (item) => (
  item?.displayName || item?.display_name || item?.name || item?.title
  || getIntegratedSearchItemId(item)
);

const createToolIdentifierArguments = (tool, identifier) => {
  const schema = tool?.input_schema;
  const properties = schema?.properties;
  const required = Array.isArray(schema?.required) ? schema.required : [];
  if (!properties || typeof properties !== 'object') return {};

  const identifierName = Object.keys(properties).find((name) => (
    ['team_id', 'teamid', 'id'].includes(name.toLowerCase())
  )) || required.find((name) => properties[name]?.type === 'string');
  return identifierName ? { [identifierName]: identifier } : {};
};

const escapeIntegratedSearchMarkdown = (value) => (
  String(value).replace(/([\\`*_{}[\]()#+.!|>-])/g, '\\$1')
);

const formatIntegratedSearchDate = (value) => {
  if (typeof value !== 'string' || !value.trim()) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value.trim()
    : new Intl.DateTimeFormat('ja-JP', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
};

const formatIntegratedSearchResults = (payload) => {
  if (!Array.isArray(payload?.results) || payload.results.length === 0) return '';

  const formattedResults = payload.results.map((result, index) => {
    if (!result || typeof result !== 'object') {
      return `${index + 1}. ${escapeIntegratedSearchMarkdown(result)}`;
    }

    const title = result.name || result.title || `検索結果 ${index + 1}`;
    const lines = [`${index + 1}. **${escapeIntegratedSearchMarkdown(title)}**`];
    const modifiedAt = formatIntegratedSearchDate(
      result.lastModifiedDateTime || result.last_modified_date_time || result.modifiedAt
    );
    const createdBy = result.createdBy || result.created_by || result.author;
    const content = result.content || result.summary || result.snippet;
    const sourceUrl = sanitizeIntegratedSearchUrl(
      result.downloadUrl || result.download_url || result.webUrl || result.url
    );

    if (modifiedAt) lines.push(`   更新: ${escapeIntegratedSearchMarkdown(modifiedAt)}`);
    if (createdBy) lines.push(`   作成者: ${escapeIntegratedSearchMarkdown(createdBy)}`);
    if (content) lines.push(`   ${escapeIntegratedSearchMarkdown(content)}`);
    if (sourceUrl) lines.push(`   [ファイルを開く](${sourceUrl})`);
    return lines.join('\n\n');
  });
  const resultCount = Number.isInteger(payload.count)
    ? payload.count
    : payload.results.length;
  return `**検索結果 ${resultCount}件**\n\n${formattedResults.join('\n\n')}`;
};

const steps = [
  {
    id: 'step1',
    label: 'Step 1 - ビジネス理解',
    endpoint: '/api/chat_step1',
    prevStep: null,
    description: 'アシスタントとの対話を通じて、データ分析で達成したい要件を明確にしましょう。'
  },
  {
    id: 'step2',
    label: 'Step 2 - データ理解',
    endpoint: '/api/chat_step2',
    prevStep: 'step1',
    description: 'アシスタントとの対話を通じて、分析に使用するデータの仕様を明確にしましょう。'
  },
  {
    id: 'step3',
    label: 'Step 3 - データ準備/モデリング/評価',
    endpoint: '/api/chat_step3',
    prevStep: 'step2',
    description: 'データ前処理、技術資産活用を最重要に、評価/可視化方針まで整理しましょう。'
  },
  {
    id: 'step4',
    label: 'Step 4 - 展開・共有',
    endpoint: '/api/chat_step4',
    prevStep: 'step3',
    description: '分析プロセス全体の保存、レポート作成、バージョン管理を整理しましょう。'
  }
];

function App() {
  const [activeTab, setActiveTab] = useState(0);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectTitle, setProjectTitle] = useState('新規プロジェクト');

  // project_idとsession_idをsessionStorageで管理（Hybrid方式）
  // - UUID生成でFrontend側で即座に利用可能
  // - Backend側でStep1/2完了時に自動永続化
  const [projectId, setProjectId] = useState(() => {
    const stored = sessionStorage.getItem('project_id');
    if (stored && isValidUUID(stored)) return stored;
    return null; // 初回はnullで、プロジェクト選択を促す
  });

  // Step別のsession_id管理（各Stepで独立したSessionを保持）
  const [sessionIdByStep, setSessionIdByStep] = useState(() => {
    const stored = sessionStorage.getItem('sessionIdByStep');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // 全てのStepに有効なUUIDがあるか確認
        if (steps.every(step => parsed[step.id] && isValidUUID(parsed[step.id]))) {
          return parsed;
        }
      } catch (e) {
        console.warn('Failed to parse sessionIdByStep from sessionStorage', e);
      }
    }
    // 新規作成
    const newSessions = steps.reduce((acc, step) => ({
      ...acc,
      [step.id]: crypto.randomUUID()
    }), {});
    sessionStorage.setItem('sessionIdByStep', JSON.stringify(newSessions));
    return newSessions;
  });

  // sessionIdByStepが変更されたらsessionStorageに保存
  useEffect(() => {
    sessionStorage.setItem('sessionIdByStep', JSON.stringify(sessionIdByStep));
  }, [sessionIdByStep]);
  const [messagesByStep, setMessagesByStep] = useState(() => (
    steps.reduce((acc, step) => ({ ...acc, [step.id]: [] }), {})
  ));
  const [inputsByStep, setInputsByStep] = useState(() => (
    steps.reduce((acc, step) => ({ ...acc, [step.id]: '' }), {})
  ));
  const [loadingByStep, setLoadingByStep] = useState(() => (
    steps.reduce((acc, step) => ({ ...acc, [step.id]: false }), {})
  ));
  const [artifactsByStep, setArtifactsByStep] = useState({
    step1: [],
    step2: [],
    step3: [],
    step4: []
  });
  const [selectedByStep, setSelectedByStep] = useState({
    step2: '',
    step3: '',
    step4: ''
  });
  const [projectFiles, setProjectFiles] = useState([]);  // プロジェクト共通の入力ファイル
  const [uploadingFiles, setUploadingFiles] = useState(false);  // ファイルアップロード中
  const [hoveredArtifact, setHoveredArtifact] = useState(null);
  const [hoveredContent, setHoveredContent] = useState(null);  // hover時のcontent取得用
  const [hoveredContentType, setHoveredContentType] = useState(null);  // hover時のcontent_type
  const [loadingContent, setLoadingContent] = useState(false);
  const [artifactPreviewCache, setArtifactPreviewCache] = useState({});  // プレビューキャッシュ
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fullscreenArtifact, setFullscreenArtifact] = useState(null);  // モーダル表示用
  const [fullscreenContent, setFullscreenContent] = useState(null);  // モーダル表示用コンテンツ
  const [fullscreenContentType, setFullscreenContentType] = useState(null);  // モーダル表示用コンテンツタイプ
  const [showSettings, setShowSettings] = useState(false);  // 設定パネルの表示/非表示
  const [showIntegratedSearch, setShowIntegratedSearch] = useState(false);
  const [integratedSearchAuthenticated, setIntegratedSearchAuthenticated] = useState(null);
  const [integratedSearchInput, setIntegratedSearchInput] = useState('');
  const [integratedSearchMessages, setIntegratedSearchMessages] = useState([]);
  const [integratedSearchLoading, setIntegratedSearchLoading] = useState(false);
  const [integratedSearchMode, setIntegratedSearchMode] = useState('agent');
  const [integratedSearchSource, setIntegratedSearchSource] = useState('auto');
  const [integratedSearchSources, setIntegratedSearchSources] = useState([]);
  const [integratedSearchTools, setIntegratedSearchTools] = useState([]);
  const [integratedSearchToolsLoading, setIntegratedSearchToolsLoading] = useState(false);
  const [integratedSearchToolsError, setIntegratedSearchToolsError] = useState('');
  const [integratedSearchToolName, setIntegratedSearchToolName] = useState('');
  const [integratedSearchToolArguments, setIntegratedSearchToolArguments] = useState('{}');
  const [integratedSearchTeams, setIntegratedSearchTeams] = useState([]);
  const [integratedSearchChannels, setIntegratedSearchChannels] = useState([]);
  const [integratedSearchTeamId, setIntegratedSearchTeamId] = useState('');
  const [integratedSearchBrowseLoading, setIntegratedSearchBrowseLoading] = useState(false);
  const [integratedSearchBrowseError, setIntegratedSearchBrowseError] = useState('');
  const integratedSearchMessagesRef = useRef(null);

  // ルーティング詳細の展開状態（キー: "stepId-msgIndex"）
  const [expandedRoutings, setExpandedRoutings] = useState({});

  // LLMが現在認識している要件定義内容（Step1/2の会話中に逐次更新）
  const [currentRequirementsByStep, setCurrentRequirementsByStep] = useState({ step1: null, step2: null });
  const [currentReqExpanded, setCurrentReqExpanded] = useState(true);
  // 案3: ask_user interrupt 中かどうか（強制完了ボタンの表示制御用）
  const [interruptTypeByStep, setInterruptTypeByStep] = useState(() => (
    steps.reduce((acc, step) => ({ ...acc, [step.id]: null }), {})
  ));

  // Q&A フィールド: バックエンドの構造化要件定義フィールド状態
  const [qaFieldsByStep, setQaFieldsByStep] = useState({});
  // Q&A バッチ: 現在表示中のフィールドインデックス
  const [qaIndexByStep, setQaIndexByStep] = useState({});
  // Q&A バッチ: ローカルに回答をバッファリング { [stepId]: { [field_key]: string } }
  const [qaAnswersByStep, setQaAnswersByStep] = useState({});
  // Q&A バッチ: 自由入力欄の値
  const [qaFreeInputByStep, setQaFreeInputByStep] = useState({});

  // LLM設定（deployment, max_tokens, temperatureをセットで管理）
  const [bulkConfig, setBulkConfig] = useState({
    deployment: '',
    max_tokens_percent: 50,  // モデルmax_tokensの50%
    temperature: 0.7
  });

  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [llmConfigByStep, setLlmConfigByStep] = useState(() => (
    steps.reduce((acc, step) => ({
      ...acc,
      [step.id]: {
        deployment: '',
        max_tokens_percent: step.id === 'step3' ? 50 : 25,  // Step3は50%、他は25%
        temperature: 0.7
      }
    }), {})
  ));

  const chatRef = useRef(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);  // Dialog要素への参照
  const leaveTimerRef = useRef(null);  // Preview消去用タイマー
  const settingsRef = useRef(null);  // 設定パネルへの参照

  const activeStep = steps[activeTab];
  const currentSessionId = useMemo(() => sessionIdByStep[activeStep.id], [sessionIdByStep, activeStep.id]);
  const messages = useMemo(() => messagesByStep[activeStep.id], [messagesByStep, activeStep.id]);
  const input = inputsByStep[activeStep.id];
  const loading = loadingByStep[activeStep.id];
  // 案3: activeStep 確定後に interrupt 状態を参照
  const interruptType = interruptTypeByStep[activeStep.id];

  // 初回表示時にプロジェクト選択モーダルを表示
  useEffect(() => {
    if (!projectId) {
      setShowProjectSelector(true);
    }
  }, [projectId]);

  // プロジェクトIDが設定されたとき（起動時のsessionStorage復元含む）タイトルを取得
  useEffect(() => {
    if (!projectId) return;
    const fetchProjectTitle = async () => {
      try {
        const res = await fetch(`/api/projects/${projectId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.title) {
            setProjectTitle(data.title);
          }
        } else if (res.status === 404) {
          // ストレージに存在しないプロジェクトIDの場合はリセットしてモーダル表示
          console.warn(`Project ${projectId} not found in storage, resetting.`);
          sessionStorage.removeItem('project_id');
          setProjectId(null);
          setProjectTitle('新規プロジェクト');
        }
      } catch (error) {
        console.error('Failed to fetch project title:', error);
      }
    };
    fetchProjectTitle();
  }, [projectId]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // プロジェクト一覧取得
  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await fetch('/api/projects?limit=20');
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // プロジェクト選択モーダル表示時にプロジェクト一覧を取得
  useEffect(() => {
    if (showProjectSelector) {
      loadProjects();
    }
  }, [showProjectSelector]);

  // 利用可能なモデル一覧を取得
  useEffect(() => {
    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const res = await fetch('/api/models');
        if (res.ok) {
          const data = await res.json();
          const deployments = data.deployments || [];

          setAvailableModels(deployments);

          // 各Stepに初期値設定（gpt-5.2を優先、なければ最初のデプロイメント）
          const preferredModel = deployments.find(d => d.id === 'gpt-5.2');
          const defaultModel = preferredModel?.id || deployments[0]?.id || '';
          if (defaultModel && Object.values(llmConfigByStep).every(c => !c.deployment)) {
            setLlmConfigByStep(
              steps.reduce((acc, step) => ({
                ...acc,
                [step.id]: {
                  deployment: defaultModel,
                  max_tokens_percent: step.id === 'step3' ? 50 : 25,
                  temperature: 0.7
                }
              }), {})
            );
            setBulkConfig(prev => ({ ...prev, deployment: defaultModel }));
          }
        }
      } catch (error) {
        console.error('Failed to load models:', error);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = () => {
      if (dropdownOpen) setDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  // 設定パネル外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettings && settingsRef.current && !settingsRef.current.contains(event.target)) {
        // 設定ボタン自体のクリックは除外（トグル動作を維持）
        const settingsButton = document.querySelector('.settings-button');
        if (settingsButton && !settingsButton.contains(event.target)) {
          setShowSettings(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // 一括設定を適用する関数
  const applyBulkConfig = () => {
    if (bulkConfig.deployment) {
      setLlmConfigByStep(
        steps.reduce((acc, step) => ({ ...acc, [step.id]: { ...bulkConfig } }), {})
      );
    }
  };

  // ファイル名をクリップボードにコピー
  const handleFileNameClick = async (fileName) => {
    try {
      await navigator.clipboard.writeText(fileName);
      // メッセージで通知（現在のStepに追加）
      const stepId = activeStep.id;
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'system', content: `📋 ファイル名をコピーしました: ${fileName}` }]
      }));
    } catch (err) {
      console.error('Failed to copy filename:', err);
      alert('コピーに失敗しました');
    }
  };

  // プロジェクトのファイル一覧を読み込む関数
  const loadProjectFiles = async () => {
    if (!projectId) return;
    try {
      const listRes = await fetch(`/api/input_files?project_id=${projectId}`);
      if (listRes.ok) {
        const data = await listRes.json();
        setProjectFiles(data.data_files || []);
      }
    } catch (error) {
      console.error('Failed to load project files:', error);
    }
  };

  // 新規プロジェクト作成
  const createNewProject = async (title = '新規プロジェクト') => {
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title })
      });
      if (res.ok) {
        const project = await res.json();
        selectProject(project.id, project.title);
        setShowProjectSelector(false);
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  // プロジェクト選択
  const selectProject = (id, title) => {
    setProjectId(id);
    setProjectTitle(title || '無題のプロジェクト');
    sessionStorage.setItem('project_id', id);

    // 新しいSession IDを生成
    const newSessions = steps.reduce((acc, step) => ({
      ...acc,
      [step.id]: crypto.randomUUID()
    }), {});
    setSessionIdByStep(newSessions);
    sessionStorage.setItem('sessionIdByStep', JSON.stringify(newSessions));

    // メッセージをクリア
    setMessagesByStep(steps.reduce((acc, step) => ({ ...acc, [step.id]: [] }), {}));
  };

  // プロジェクトIDが変わったらファイル一覧を読み込む
  useEffect(() => {
    if (projectId) {
      loadProjectFiles();
    }
  }, [projectId]);

  useEffect(() => {
    const fetchArtifacts = async () => {
      if (!activeStep.prevStep || !projectId) return;
      // session_idを指定せず全Session分のArtifactを取得（選択肢として表示）
      // limit=10で最新10件に制限（パフォーマンス対策）
      const res = await fetch(`/api/artifacts?step=${activeStep.prevStep}&project_id=${projectId}&limit=10`);
      if (!res.ok) return;
      const data = await res.json();
      const items = data.artifacts || [];
      setArtifactsByStep(prev => ({ ...prev, [activeStep.prevStep]: items }));

      setSelectedByStep(prev => {
        if (!prev[activeStep.id] && items.length > 0) {
          const firstPath = items[0].metadata?.path;
          return { ...prev, [activeStep.id]: firstPath };
        }
        return prev;
      });
    };

    const fetchInputFiles = async () => {
      if (!projectId) return;
      const res = await fetch(`/api/input_files?project_id=${projectId}`);
      if (!res.ok) return;
      const data = await res.json();
      const items = data.data_files || [];
      setProjectFiles(items);
    };

    fetchArtifacts();
    fetchInputFiles();
  }, [activeTab, activeStep.prevStep, activeStep.id, projectId]);

  const formatArtifactLabel = (item) => {
    const title = item.title || '無題';
    const ts = item.created_at || item.last_modified || '';

    // UTC → JST変換
    let jstTime = '';
    if (ts) {
      try {
        const date = new Date(ts);
        // JST (UTC+9)に変換して表示
        jstTime = date.toLocaleString('ja-JP', {
          timeZone: 'Asia/Tokyo',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        jstTime = ts;
      }
    }

    // タイトルと日時のみ表示（summaryは長すぎて見づらいため）
    return `${title} | ${jstTime}`;
  };


  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFiles(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);
      // step/session_idは不要（dataフォルダはproject共有）

      const res = await fetch('/api/upload_input', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('Upload failed:', errorData);
        alert(`ファイルアップロードに失敗しました: ${errorData.error || 'Unknown error'}`);
        return;
      }

      // 入力ファイル一覧を再取得
      await loadProjectFiles();

      // 現在のStepのメッセージに追加
      const stepId = activeStep.id;
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'system', content: `📎 ファイルをアップロードしました: ${file.name}` }]
      }));
    } catch (err) {
      console.error('Upload error:', err);
      alert('ファイルアップロードに失敗しました');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (overrideMessage) => {
    const messageText = overrideMessage || input;
    if (!messageText) return;

    const stepId = activeStep.id;
    const endpoint = activeStep.endpoint;
    const selectedArtifacts = activeStep.prevStep && selectedByStep[stepId]
      ? [selectedByStep[stepId]]
      : [];

    // LLM設定取得（Step毎の設定）
    const config = llmConfigByStep[stepId];
    if (!config.deployment) {
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: 'エラー: デプロイメントが選択されていません' }]
      }));
      return;
    }

    // max_tokensの実際の値を計算（モデルのmax_tokens * percent / 100）
    const modelInfo = availableModels.find(m => m.id === config.deployment);
    const actualMaxTokens = modelInfo ? Math.floor(modelInfo.max_tokens * config.max_tokens_percent / 100) : 8192;

    setMessagesByStep(prev => ({
      ...prev,
      [stepId]: [...prev[stepId], { role: 'user', content: messageText }]
    }));
    setLoadingByStep(prev => ({ ...prev, [stepId]: true }));
    setInputsByStep(prev => ({ ...prev, [stepId]: '' }));
    // 案3: 新しい送信時は interrupt 状態をリセット
    setInterruptTypeByStep(prev => ({ ...prev, [stepId]: null }));

    // ポーリング中はfinallyでloadingをfalseにしないためのフラグ
    let handledByPoll = false;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          selected_artifacts: selectedArtifacts,
          project_id: projectId,
          session_id: currentSessionId,
          deployment_id: config.deployment,
          max_tokens: actualMaxTokens,
          temperature: config.temperature
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();

      // --- Step3 非同期ジョブモード（202 Accepted + job_id）---
      // Azure App Service 240秒タイムアウト対策: ジョブをポーリングで待機
      if (res.status === 202 && data.job_id) {
        const jobId = data.job_id;
        const pollStartTime = Date.now();
        const POLL_INTERVAL_MS = 3000;
        const MAX_WAIT_MS = 30 * 60 * 1000; // 最大30分待機

        // 暫定メッセージを追加（jobIdをキーとして後で置き換える）
        setMessagesByStep(prev => ({
          ...prev,
          [stepId]: [...prev[stepId], {
            role: 'assistant',
            content: '⏳ 分析を開始しました。しばらくお待ちください...',
            _jobId: jobId
          }]
        }));

        handledByPoll = true;

        // バックグラウンドでポーリング（awaitしない）
        (async () => {
          try {
            while (true) {
              await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));

              const elapsed = Math.round((Date.now() - pollStartTime) / 1000);

              // タイムアウト保護
              if (Date.now() - pollStartTime > MAX_WAIT_MS) {
                setMessagesByStep(prev => ({
                  ...prev,
                  [stepId]: prev[stepId].map(m =>
                    m._jobId === jobId
                      ? { role: 'assistant', content: '⏳ 分析がタイムアウトしました（30分超過）。再試行してください。' }
                      : m
                  )
                }));
                break;
              }

              let jobData;
              try {
                const jobRes = await fetch(`/api/jobs/${jobId}`);
                // 404 = サーバー再起動でジョブが消えた
                if (jobRes.status === 404) {
                  setMessagesByStep(prev => ({
                    ...prev,
                    [stepId]: prev[stepId].map(m =>
                      m._jobId === jobId
                        ? { role: 'assistant', content: '❌ サーバーが再起動したためジョブが失われました。再試行してください。' }
                        : m
                    )
                  }));
                  setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
                  break;
                }
                jobData = await jobRes.json();
              } catch (pollErr) {
                console.warn('Polling fetch error (retrying):', pollErr);
                // ネットワーク瞬断は無視して続行
                continue;
              }

              if (jobData.status === 'done') {
                // 完了: 暫定メッセージを結果で置き換え
                setMessagesByStep(prev => ({
                  ...prev,
                  [stepId]: prev[stepId].map(m =>
                    m._jobId === jobId
                      ? { role: 'assistant', content: jobData.message, artifacts: jobData.artifacts || null }
                      : m
                  )
                }));
                // 成果物一覧を更新
                if (jobData.artifacts?.length > 0) {
                  const artifactRes = await fetch(`/api/artifacts?step=${stepId}&project_id=${projectId}&session_id=${currentSessionId}`);
                  if (artifactRes.ok) {
                    const artifactsData = await artifactRes.json();
                    setArtifactsByStep(prev => ({ ...prev, [stepId]: artifactsData.artifacts || [] }));
                  }
                }
                setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
                break;

              } else if (jobData.status === 'failed') {
                // 失敗: エラーメッセージで置き換え
                setMessagesByStep(prev => ({
                  ...prev,
                  [stepId]: prev[stepId].map(m =>
                    m._jobId === jobId
                      ? { role: 'assistant', content: `❌ 分析に失敗しました: ${jobData.error}` }
                      : m
                  )
                }));
                setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
                break;

              } else {
                // 実行中: 経過時間を更新
                setMessagesByStep(prev => ({
                  ...prev,
                  [stepId]: prev[stepId].map(m =>
                    m._jobId === jobId
                      ? { role: 'assistant', content: `⏳ 分析中... (${elapsed}秒経過)`, _jobId: jobId }
                      : m
                  )
                }));
              }
            }
          } catch (pollLoopErr) {
            console.error('Polling loop error:', pollLoopErr);
            setMessagesByStep(prev => ({
              ...prev,
              [stepId]: prev[stepId].map(m =>
                m._jobId === jobId
                  ? { role: 'assistant', content: `❌ ポーリング中にエラーが発生しました: ${pollLoopErr.message}` }
                  : m
              )
            }));
            setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
          }
        })();

        return; // finallyを経由してhandledByPollを評価させる
      }

      // --- 通常モード（Step1/2/4）---
      const botMsg = data.message;
      const artifact = data.artifact || null;
      const artifacts = data.artifacts || null;
      const referencedFiles = data.referenced_files || null;
      const nodeTrace = data.node_trace || null;
      const qaField = data.qa_field || null;

      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: botMsg, artifact, artifacts, referencedFiles, nodeTrace, qaField }]
      }));

      // Step1/2: LLMが現在認識している要件定義内容を更新して表示
      if (data.current_requirements && (stepId === 'step1' || stepId === 'step2')) {
        setCurrentRequirementsByStep(prev => ({ ...prev, [stepId]: data.current_requirements }));
        setCurrentReqExpanded(true);
      }

      // 案3: ask_user interrupt 中かどうかを状態に反映
      setInterruptTypeByStep(prev => ({ ...prev, [stepId]: data.interrupt_type || null }));

      // Q&A フィールドデータの管理
      if (data.qa_fields && data.qa_fields.length > 0) {
        setQaFieldsByStep(prev => ({ ...prev, [stepId]: data.qa_fields }));
      } else if (!data.interrupt_type) {
        // interrupt 完了時（要件定義確定）は Q&A フィールドをクリア
        setQaFieldsByStep(prev => ({ ...prev, [stepId]: null }));
      }

      // メッセージ表示完了後、ローディングを解除（アーティファクト取得は裏で行う）
      if (!handledByPoll) {
        setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
      }

      // 成果物が作成された場合、ドロップダウンリストを再取得
      if (artifact || artifacts) {
        const artifactRes = await fetch(`/api/artifacts?step=${stepId}&project_id=${projectId}&session_id=${currentSessionId}`);
        if (artifactRes.ok) {
          const artifactsData = await artifactRes.json();
          setArtifactsByStep(prev => ({ ...prev, [stepId]: artifactsData.artifacts || [] }));
        }
      }
    } catch (error) {
      console.error('API Error:', error);
      // ネットワーク接続失敗（バックエンド未起動・到達不能）の場合は分かりやすいメッセージに変換
      const isNetworkError =
        error instanceof TypeError &&
        (error.message === 'Failed to fetch' || error.message.includes('NetworkError') || error.message.includes('network'));
      const displayMessage = isNetworkError
        ? `⚠️ バックエンドサーバーに接続できませんでした。\n` +
        `サーバーが起動しているか、ネットワーク接続を確認してください。\n\n` +
        `（技術情報: ${error.message}）`
        : `エラー: ${error.message}`;
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: displayMessage }]
      }));
      setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
    }
  };

  // 案3: 強制完了ボタンのハンドラ (__PROCEED__ をお送りする)
  const handleForceComplete = async () => {
    const stepId = activeStep.id;
    const endpoint = activeStep.endpoint;
    const config = llmConfigByStep[stepId];
    const modelInfo = availableModels.find(m => m.id === config.deployment);
    const actualMaxTokens = modelInfo ? Math.floor(modelInfo.max_tokens * config.max_tokens_percent / 100) : 8192;

    setMessagesByStep(prev => ({
      ...prev,
      [stepId]: [...prev[stepId], { role: 'user', content: '次のステップへ進みます' }]
    }));
    setLoadingByStep(prev => ({ ...prev, [stepId]: true }));
    setInterruptTypeByStep(prev => ({ ...prev, [stepId]: null }));

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: '__PROCEED__',
          project_id: projectId,
          session_id: currentSessionId,
          deployment_id: config.deployment,
          max_tokens: actualMaxTokens,
          temperature: config.temperature
        })
      });
      const data = await res.json();
      const artifact = data.artifact || null;
      const nodeTrace = data.node_trace || null;
      const qaField = data.qa_field || null;
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: data.message, artifact, nodeTrace, qaField }]
      }));
      if (data.current_requirements && (stepId === 'step1' || stepId === 'step2')) {
        setCurrentRequirementsByStep(prev => ({ ...prev, [stepId]: data.current_requirements }));
        setCurrentReqExpanded(true);
      }
      // Q&A フィールドをクリア（強制完了時は全フィールド確定済み）
      setQaFieldsByStep(prev => ({ ...prev, [stepId]: null }));
      if (artifact) {
        const artifactRes = await fetch(`/api/artifacts?step=${stepId}&project_id=${projectId}&session_id=${currentSessionId}`);
        if (artifactRes.ok) {
          const artifactsData = await artifactRes.json();
          setArtifactsByStep(prev => ({ ...prev, [stepId]: artifactsData.artifacts || [] }));
        }
      }
    } catch (error) {
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: `エラー: ${error.message}` }]
      }));
    } finally {
      setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
    }
  };

  // === Q&A バッチ操作ハンドラ ===

  // Q&A ナビゲーション（←→）
  const handleQaNavigate = (stepId, direction) => {
    const fields = qaFieldsByStep[stepId];
    if (!fields || fields.length === 0) return;
    const currentIdx = qaIndexByStep[stepId] || 0;
    const newIdx = Math.max(0, Math.min(fields.length - 1, currentIdx + direction));
    setQaIndexByStep(prev => ({ ...prev, [stepId]: newIdx }));
    setQaFreeInputByStep(prev => ({ ...prev, [stepId]: '' }));
  };

  // Q&A: 候補選択（トグル + 自動次へ）
  const handleQaSelectSuggestion = (stepId, fieldKey, suggestion) => {
    setQaAnswersByStep(prev => {
      const stepAnswers = prev[stepId] || {};
      // 同じ候補をクリック → 解除（次へ進まない）
      const current = stepAnswers[fieldKey];
      if (current === suggestion) {
        const { [fieldKey]: _, ...rest } = stepAnswers;
        return { ...prev, [stepId]: rest };
      }
      return { ...prev, [stepId]: { ...stepAnswers, [fieldKey]: suggestion } };
    });
    // 選択時のみ自動で次の未回答フィールドへ進む
    const fields = qaFieldsByStep[stepId];
    const answers = qaAnswersByStep[stepId] || {};
    const currentAnswer = answers[fieldKey];
    if (currentAnswer === suggestion) return; // 解除時は進まない
    if (fields) {
      const currentIdx = qaIndexByStep[stepId] || 0;
      // 次の未回答フィールドを探す
      let nextIdx = -1;
      for (let i = currentIdx + 1; i < fields.length; i++) {
        if (!answers[fields[i].field_key] && fields[i].status !== 'user_provided') {
          nextIdx = i;
          break;
        }
      }
      if (nextIdx >= 0) {
        setQaIndexByStep(prev => ({ ...prev, [stepId]: nextIdx }));
        setQaFreeInputByStep(prev => ({ ...prev, [stepId]: '' }));
      }
    }
  };

  // Q&A: 自由入力で回答確定
  const handleQaFreeSubmit = (stepId, fieldKey) => {
    const text = (qaFreeInputByStep[stepId] || '').trim();
    if (!text) return;
    setQaAnswersByStep(prev => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), [fieldKey]: text }
    }));
    setQaFreeInputByStep(prev => ({ ...prev, [stepId]: '' }));
    // 自動で次のフィールドへ
    const fields = qaFieldsByStep[stepId];
    const currentIdx = qaIndexByStep[stepId] || 0;
    if (fields && currentIdx < fields.length - 1) {
      setQaIndexByStep(prev => ({ ...prev, [stepId]: currentIdx + 1 }));
    }
  };

  // Q&A: スキップ（次のフィールドへ移動）
  const handleQaFieldSkip = (stepId, fieldKey) => {
    setQaAnswersByStep(prev => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), [fieldKey]: '__SKIP__' }
    }));
    const fields = qaFieldsByStep[stepId];
    const currentIdx = qaIndexByStep[stepId] || 0;
    if (fields && currentIdx < fields.length - 1) {
      setQaIndexByStep(prev => ({ ...prev, [stepId]: currentIdx + 1 }));
    }
  };

  // Q&A: 回答クリア
  const handleQaClearAnswer = (stepId, fieldKey) => {
    setQaAnswersByStep(prev => {
      const stepAnswers = { ...(prev[stepId] || {}) };
      delete stepAnswers[fieldKey];
      return { ...prev, [stepId]: stepAnswers };
    });
  };

  // Q&A: 一括送信
  const handleQaBatchSubmit = async (stepId) => {
    const fields = qaFieldsByStep[stepId];
    const answers = qaAnswersByStep[stepId] || {};
    if (!fields) return;

    // 回答をJSON形式でまとめる
    const batchPayload = fields.map(f => ({
      field_key: f.field_key,
      label: f.label,
      answer: answers[f.field_key] || null
    })).filter(item => item.answer && item.answer !== '__SKIP__');

    const skipped = fields.map(f => ({
      field_key: f.field_key,
      label: f.label
    })).filter(f => answers[f.field_key] === '__SKIP__');

    const batchMessage = `__BATCH_SUBMIT__${JSON.stringify({ answers: batchPayload, skipped })}`;

    // 表示用メッセージ（表形式で回答内容を表示）
    let displayMsg = '**回答内容:**\n\n| 項目 | 回答 |\n|------|------|\n';
    batchPayload.forEach(item => {
      displayMsg += `| ${item.label} | ${item.answer} |\n`;
    });
    if (skipped.length > 0) {
      skipped.forEach(item => {
        displayMsg += `| ${item.label} | _(スキップ)_ |\n`;
      });
    }

    const endpoint = activeStep.endpoint;
    const config = llmConfigByStep[stepId];
    const modelInfo = availableModels.find(m => m.id === config.deployment);
    const actualMaxTokens = modelInfo ? Math.floor(modelInfo.max_tokens * config.max_tokens_percent / 100) : 8192;

    setMessagesByStep(prev => ({
      ...prev,
      [stepId]: [...prev[stepId], { role: 'user', content: displayMsg }]
    }));
    setLoadingByStep(prev => ({ ...prev, [stepId]: true }));
    setInterruptTypeByStep(prev => ({ ...prev, [stepId]: null }));
    // Q&A 状態をクリア
    setQaAnswersByStep(prev => ({ ...prev, [stepId]: {} }));
    setQaIndexByStep(prev => ({ ...prev, [stepId]: 0 }));
    setQaFreeInputByStep(prev => ({ ...prev, [stepId]: '' }));

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: batchMessage,
          project_id: projectId,
          session_id: currentSessionId,
          deployment_id: config.deployment,
          max_tokens: actualMaxTokens,
          temperature: config.temperature
        })
      });
      const data = await res.json();
      const artifact = data.artifact || null;
      const artifacts = data.artifacts || null;
      const nodeTrace = data.node_trace || null;
      const qaField = data.qa_field || null;
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: data.message, artifact, artifacts, nodeTrace, qaField }]
      }));
      if (data.current_requirements && (stepId === 'step1' || stepId === 'step2')) {
        setCurrentRequirementsByStep(prev => ({ ...prev, [stepId]: data.current_requirements }));
      }
      setInterruptTypeByStep(prev => ({ ...prev, [stepId]: data.interrupt_type || null }));
      if (data.qa_fields && data.qa_fields.length > 0) {
        setQaFieldsByStep(prev => ({ ...prev, [stepId]: data.qa_fields }));
      } else if (!data.interrupt_type) {
        setQaFieldsByStep(prev => ({ ...prev, [stepId]: null }));
      }
      // メッセージ表示完了後、ローディングを解除（アーティファクト取得は裏で行う）
      setLoadingByStep(prev => ({ ...prev, [stepId]: false }));

      if (artifact || artifacts) {
        const artifactRes = await fetch(`/api/artifacts?step=${stepId}&project_id=${projectId}&session_id=${currentSessionId}`);
        if (artifactRes.ok) {
          const artifactsData = await artifactRes.json();
          setArtifactsByStep(prev => ({ ...prev, [stepId]: artifactsData.artifacts || [] }));
        }
      }
    } catch (error) {
      setMessagesByStep(prev => ({
        ...prev,
        [stepId]: [...prev[stepId], { role: 'assistant', content: `エラー: ${error.message}` }]
      }));
      setLoadingByStep(prev => ({ ...prev, [stepId]: false }));
    }
  };

  const handleNewTrial = () => {
    const confirmed = window.confirm(
      '現在のSessionを終了し、新しい試行を開始しますか？\n' +
      '（現在の対話履歴は失われますが、成果物は保存されています）'
    );
    if (!confirmed) return;

    const newSessionId = crypto.randomUUID();
    setSessionIdByStep(prev => ({
      ...prev,
      [activeStep.id]: newSessionId
    }));

    // メッセージもクリア
    setMessagesByStep(prev => ({
      ...prev,
      [activeStep.id]: []
    }));

    // Q&A フィールドもクリア
    setQaFieldsByStep(prev => ({ ...prev, [activeStep.id]: null }));
  };

  // Hover時にcontentを非同期取得する関数
  const handleArtifactHover = async (item) => {
    setHoveredArtifact(item);

    const contentPath = item?.content?.path;
    if (!contentPath) {
      return;
    }

    // キャッシュチェック
    if (artifactPreviewCache[contentPath]) {
      const cached = artifactPreviewCache[contentPath];
      setHoveredContent(cached.content);
      setHoveredContentType(cached.contentType || 'text/markdown');
      return;
    }

    // ローディング中は重複呼び出しを防止
    if (loadingContent) return;

    setHoveredContent(null);
    setHoveredContentType(null);
    setLoadingContent(true);
    try {
      const res = await fetch(`/api/artifacts/content?path=${encodeURIComponent(contentPath)}`);

      if (res && res.ok) {
        const contentType = item?.content?.type || res.headers.get('content-type') || 'text/markdown';
        const content = await res.text();
        setHoveredContent(content);
        setHoveredContentType(contentType);
        // キャッシュに保存
        setArtifactPreviewCache(prev => ({
          ...prev,
          [contentPath]: { content, contentType }
        }));
      } else {
        setHoveredContent(item.summary || '内容の取得に失敗しました');
        setHoveredContentType('text/markdown');
      }
    } catch (err) {
      console.error('Failed to fetch artifact content:', err);
      setHoveredContent(item.summary || '内容の取得に失敗しました');
      setHoveredContentType('text/markdown');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleArtifactLeave = () => {
    // 遅延して消す（Preview上にマウスが移動する時間を稼ぐ）
    leaveTimerRef.current = setTimeout(() => {
      setHoveredArtifact(null);
      setHoveredContent(null);
    }, 200);  // 200ms遅延
  };

  const handleArtifactEnter = () => {
    // Leaveタイマーをキャンセル
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const handleArtifactLinkClick = async (artifact) => {
    const contentPath = artifact?.content?.path;

    // キャッシュがあればそのままモーダルを開く
    if (contentPath && artifactPreviewCache[contentPath]) {
      const cached = artifactPreviewCache[contentPath];
      setFullscreenArtifact(artifact);
      setFullscreenContent(cached.content);
      setFullscreenContentType(cached.contentType || 'text/markdown');
      if (dialogRef.current) dialogRef.current.showModal();
      return;
    }

    // コンテンツを取得してからモーダルを開く
    let content = artifact?.summary || '';
    let contentType = artifact?.content?.type || 'text/markdown';

    if (contentPath) {
      try {
        const res = await fetch(`/api/artifacts/content?path=${encodeURIComponent(contentPath)}`);
        if (res.ok) {
          contentType = artifact?.content?.type || res.headers.get('content-type') || 'text/markdown';
          content = await res.text();
          // キャッシュにも保存
          setArtifactPreviewCache(prev => ({
            ...prev,
            [contentPath]: { content, contentType }
          }));
        }
      } catch (err) {
        console.error('Failed to fetch artifact content:', err);
      }
    }

    setFullscreenArtifact(artifact);
    setFullscreenContent(content);
    setFullscreenContentType(contentType);
    if (dialogRef.current) dialogRef.current.showModal();
  };

  // プレビューをクリックしてモーダル表示
  const handlePreviewClick = () => {
    if (!hoveredArtifact) return;
    setFullscreenArtifact(hoveredArtifact);
    setFullscreenContent(hoveredContent);
    setFullscreenContentType(hoveredContentType);
    // Hoverプレビューを閉じる
    setHoveredArtifact(null);
    setHoveredContent(null);
    setHoveredContentType(null);
    // ダイアログを表示
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  };

  // モーダルダイアログを閉じる
  const handleCloseDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
    setFullscreenArtifact(null);
    setFullscreenContent(null);
    setFullscreenContentType(null);
  };

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && dialogRef.current && dialogRef.current.open) {
        handleCloseDialog();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const renderArtifactSelector = () => {
    if (!activeStep.prevStep) return null;
    const items = artifactsByStep[activeStep.prevStep] || [];
    const getItemPath = (item) => item.metadata?.path;
    const selectedItem = items.find(item =>
      getItemPath(item) === (selectedByStep[activeStep.id] || getItemPath(items[0]))
    );

    return (
      <div className="section">
        <div className="section-title">前工程の成果物（最新が既定）</div>
        <div className="dropdown-container">
          {/* カスタムドロップダウン */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
            onMouseEnter={() => {
              handleArtifactEnter();
              if (selectedItem) handleArtifactHover(selectedItem);
            }}
            onMouseLeave={handleArtifactLeave}
            className="dropdown-header"
          >
            {selectedItem ? formatArtifactLabel(selectedItem) : '成果物がありません'}
            <span className="dropdown-arrow">{dropdownOpen ? '▲' : '▼'}</span>
          </div>

          {/* ドロップダウンメニュー */}
          {dropdownOpen && items.length > 0 && (
            <div className="dropdown-menu">
              {items.map(item => {
                const itemPath = getItemPath(item);
                return (
                  <div
                    key={item.id || itemPath}
                    onClick={() => {
                      setSelectedByStep(prev => ({ ...prev, [activeStep.id]: itemPath }));
                      setDropdownOpen(false);
                      handleArtifactLeave(); // クリック時にhover状態をクリア
                    }}
                    onMouseEnter={() => {
                      handleArtifactEnter();
                      handleArtifactHover(item);
                    }}
                    onMouseLeave={handleArtifactLeave}
                    className={`dropdown-item ${itemPath === getItemPath(selectedItem) ? 'selected' : ''}`}
                  >
                    {formatArtifactLabel(item)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNewTrialButton = () => (
    <div className="section">
      <button onClick={handleNewTrial} className="new-trial-button">
        🔄 新しい試行を開始
      </button>
      <span className="session-id-display">
        Session ID: {currentSessionId.substring(0, 8)}...
      </span>
    </div>
  );

  useEffect(() => {
    const messagesElement = integratedSearchMessagesRef.current;
    if (messagesElement) {
      messagesElement.scrollTo({
        top: messagesElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [integratedSearchMessages, integratedSearchLoading]);

  const loadIntegratedSearchTools = async () => {
    setIntegratedSearchToolsLoading(true);
    setIntegratedSearchToolsError('');
    try {
      const response = await fetch('/api/integrated-search/tools');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'MCP Tool 一覧を取得できませんでした。');
      }
      const tools = Array.isArray(data.tools) ? data.tools : [];
      const sources = Array.isArray(data.sources) ? data.sources : [];
      setIntegratedSearchTools(tools);
      setIntegratedSearchSources(sources);
      const selectedSource = sources.find(
        (source) => source.value === integratedSearchSource && source.available
      );
      if (!selectedSource) {
        const firstAvailable = sources.find((source) => source.available);
        setIntegratedSearchSource(firstAvailable?.value || '');
      }
      const preferredTool = tools.find((tool) => (
        typeof tool.name === 'string' && tool.name.includes('tavily_search')
      )) || tools[0];
      if (preferredTool) {
        setIntegratedSearchToolName(preferredTool.name);
        setIntegratedSearchToolArguments(createIntegratedSearchArguments(preferredTool));
      } else {
        setIntegratedSearchToolName('');
        setIntegratedSearchToolArguments('{}');
      }
    } catch (error) {
      console.error('[IntegratedSearch] Failed to load MCP tools:', error);
      setIntegratedSearchToolsError(error.message);
      setIntegratedSearchSources([]);
    } finally {
      setIntegratedSearchToolsLoading(false);
    }
  };

  const openIntegratedSearch = async () => {
    setShowIntegratedSearch(true);
    try {
      const response = await fetch('/api/auth/status');
      const data = await response.json();
      const authenticated = Boolean(data.authenticated);
      setIntegratedSearchAuthenticated(authenticated);
      if (authenticated) await loadIntegratedSearchTools();
    } catch (error) {
      console.error('Failed to get AgentCore authentication status:', error);
      setIntegratedSearchAuthenticated(false);
    }
  };

  const selectIntegratedSearchTool = (toolName) => {
    const tool = integratedSearchTools.find((candidate) => candidate.name === toolName);
    setIntegratedSearchToolName(toolName);
    setIntegratedSearchToolArguments(createIntegratedSearchArguments(tool));
  };

  const selectIntegratedSearchMode = async (mode) => {
    setIntegratedSearchMode(mode);
    if ((mode === 'search' || mode === 'tools') && integratedSearchTools.length === 0) {
      await loadIntegratedSearchTools();
    }
  };

  const requestIntegratedSearchTool = async (toolName, argumentsObject) => {
    const response = await fetch('/api/integrated-search/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool_name: toolName, arguments: argumentsObject })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const messages = [data.error || 'MCP Tool の実行に失敗しました。'];
      if (data.detail) messages.push(data.detail);
      const error = new Error(messages.join('\n\n'));
      error.diagnostics = data;
      throw error;
    }
    return data;
  };

  const loadIntegratedSearchTeams = async (source) => {
    const toolName = source?.tools?.list_teams;
    const tool = integratedSearchTools.find((candidate) => candidate.name === toolName);
    if (!toolName || !tool) return;
    setIntegratedSearchBrowseLoading(true);
    setIntegratedSearchBrowseError('');
    setIntegratedSearchTeams([]);
    setIntegratedSearchChannels([]);
    setIntegratedSearchTeamId('');
    try {
      const argumentsObject = JSON.parse(createIntegratedSearchArguments(tool));
      const data = await requestIntegratedSearchTool(toolName, argumentsObject);
      setIntegratedSearchTeams(extractIntegratedSearchCollection(data.responses, 'teams'));
    } catch (error) {
      setIntegratedSearchBrowseError(error.message);
    } finally {
      setIntegratedSearchBrowseLoading(false);
    }
  };

  const selectIntegratedSearchSource = async (sourceValue) => {
    setIntegratedSearchSource(sourceValue);
    setIntegratedSearchBrowseError('');
    const source = integratedSearchSources.find(
      (candidate) => candidate.value === sourceValue
    );
    if (source?.mode === 'browse') await loadIntegratedSearchTeams(source);
  };

  const selectIntegratedSearchTeam = async (teamId) => {
    setIntegratedSearchTeamId(teamId);
    setIntegratedSearchChannels([]);
    setIntegratedSearchBrowseError('');
    if (!teamId) return;
    const source = integratedSearchSources.find(
      (candidate) => candidate.value === 'teams'
    );
    const toolName = source?.tools?.list_channels;
    const tool = integratedSearchTools.find((candidate) => candidate.name === toolName);
    if (!toolName || !tool) return;
    setIntegratedSearchBrowseLoading(true);
    try {
      const data = await requestIntegratedSearchTool(
        toolName,
        createToolIdentifierArguments(tool, teamId)
      );
      setIntegratedSearchChannels(
        extractIntegratedSearchCollection(data.responses, 'channels')
      );
    } catch (error) {
      setIntegratedSearchBrowseError(error.message);
    } finally {
      setIntegratedSearchBrowseLoading(false);
    }
  };

  const extractIntegratedSearchText = (responses) => {
    if (typeof responses === 'string') {
      const trimmedResponse = responses.trim();
      try {
        const parsedResponse = JSON.parse(trimmedResponse);
        if (parsedResponse && typeof parsedResponse === 'object') {
          return extractIntegratedSearchText(parsedResponse);
        }
      } catch (error) {
        console.debug('[IntegratedSearch] MCP text is not JSON:', error);
      }
      return trimmedResponse;
    }

    if (responses && typeof responses === 'object' && !Array.isArray(responses)) {
      if (typeof responses.answer === 'string' && responses.answer.trim()) {
        return responses.answer.trim();
      }

      const formattedResults = formatIntegratedSearchResults(responses);
      if (formattedResults) return formattedResults;
    }

    const texts = [];
    const collectText = (value) => {
      if (Array.isArray(value)) {
        value.forEach(collectText);
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([key, child]) => {
          if (key === 'text' && typeof child === 'string') {
            texts.push(child);
          } else {
            collectText(child);
          }
        });
      }
    };
    collectText(responses);
    if (texts.length > 0) {
      return texts.join('\n\n');
    }
    if (responses === null || responses === undefined) {
      return '回答データがありませんでした。';
    }
    try {
      return `未対応の応答形式です。\n\n${JSON.stringify(responses, null, 2)}`;
    } catch (error) {
      console.warn('[IntegratedSearch] Failed to serialize responses:', error);
      return '回答の形式を解析できませんでした。';
    }
  };

  const sendIntegratedSearch = async () => {
    const isToolMode = integratedSearchMode === 'tools';
    const isAgentMode = integratedSearchMode === 'agent';
    const inputText = isToolMode
      ? integratedSearchToolArguments.trim()
      : integratedSearchInput.trim();
    if (!inputText || integratedSearchLoading) return;
    if (isToolMode && !integratedSearchToolName) return;

    let argumentsObject = null;
    if (isToolMode) {
      try {
        argumentsObject = JSON.parse(inputText);
        if (!argumentsObject || Array.isArray(argumentsObject) || typeof argumentsObject !== 'object') {
          throw new Error('JSON object is required');
        }
      } catch (error) {
        setIntegratedSearchMessages(prev => [
          ...prev,
          {
            role: 'error',
            content: `引数を JSON オブジェクトとして解析できませんでした。\n\n${error.message}`,
            toolName: integratedSearchToolName,
            mode: 'tools'
          }
        ]);
        return;
      }
    }

    setIntegratedSearchMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: isToolMode ? JSON.stringify(argumentsObject, null, 2) : inputText,
        toolName: isToolMode ? integratedSearchToolName : null,
        searchSource: isToolMode
          ? null
          : isAgentMode ? 'agent' : integratedSearchSource,
        mode: integratedSearchMode
      }
    ]);
    if (!isToolMode) setIntegratedSearchInput('');
    setIntegratedSearchLoading(true);
    const startedAt = performance.now();
    console.info('[IntegratedSearch] Request started', {
      mode: integratedSearchMode,
      toolName: isToolMode ? integratedSearchToolName : null,
      inputLength: inputText.length,
      argumentNames: isToolMode ? Object.keys(argumentsObject) : []
    });
    try {
      const response = await fetch(
        isAgentMode ? '/api/integrated-search/agent' : '/api/integrated-search/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isAgentMode
          ? { query: inputText }
          : isToolMode
            ? { tool_name: integratedSearchToolName, arguments: argumentsObject }
            : {
              query: inputText,
              ...(integratedSearchSource === 'auto'
                ? {}
                : { search_source: integratedSearchSource })
            })
      }
      );
      const data = await response.json().catch(() => ({}));
      console.info('[IntegratedSearch] Response received', {
        status: response.status,
        ok: response.ok,
        elapsedMs: Math.round(performance.now() - startedAt),
        responseKeys: data && typeof data === 'object' ? Object.keys(data) : [],
        toolName: data?.tool_name || null,
        errorCode: data?.error_code || null,
        responseType: Array.isArray(data?.responses) ? 'array' : typeof data?.responses,
        resultCount: Array.isArray(data?.responses?.results)
          ? data.responses.results.length
          : null
      });
      if (response.status === 401) {
        setIntegratedSearchAuthenticated(false);
        throw new Error('Entra ID サインインが必要です。');
      }
      if (!response.ok) {
        const errorDetails = [data.error || '統合検索に失敗しました。'];
        errorDetails.push(`HTTP status: ${response.status}`);
        if (data.error_code) errorDetails.push(`Error code: ${data.error_code}`);
        if (data.tool_name) errorDetails.push(`Tool: ${data.tool_name}`);
        if (data.detail) errorDetails.push(`Detail: ${data.detail}`);
        const requestError = new Error(errorDetails.join('\n\n'));
        requestError.diagnostics = data;
        throw requestError;
      }
      setIntegratedSearchMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: extractIntegratedSearchText(data.responses),
          toolName: isToolMode ? data.tool_name : null,
          searchSource: isToolMode
            ? null
            : isAgentMode ? 'agent' : data.search_source || integratedSearchSource,
          mode: integratedSearchMode
        }
      ]);
    } catch (error) {
      console.error('[IntegratedSearch] Request failed', {
        elapsedMs: Math.round(performance.now() - startedAt),
        error
      });
      setIntegratedSearchMessages(prev => [
        ...prev,
        {
          role: 'error',
          content: error.message,
          toolName: isToolMode
            ? error.diagnostics?.tool_name || integratedSearchToolName
            : null,
          searchSource: isToolMode
            ? null
            : isAgentMode ? 'agent' : integratedSearchSource,
          mode: integratedSearchMode
        }
      ]);
    } finally {
      setIntegratedSearchLoading(false);
    }
  };

  const selectedIntegratedSearchTool = integratedSearchTools.find(
    (tool) => tool.name === integratedSearchToolName
  );

  return (
    <div className="app-container">
      <div className="app-header">
        <h2 className="app-title">データ分析アシスタント</h2>
        <div className="project-info">
          <span className="project-title">{projectTitle}</span>
          <button
            className="change-project-button"
            onClick={() => setShowProjectSelector(true)}
          >
            プロジェクト切替
          </button>
          <button
            className="integrated-search-button"
            onClick={openIntegratedSearch}
          >
            統合検索
          </button>
          <button
            className="settings-button"
            onClick={() => setShowSettings(!showSettings)}
            title="設定"
          >
            ⚙️ 設定
          </button>
        </div>
      </div>

      {/* プロジェクト選択モーダル */}
      {showProjectSelector && (
        <div className="modal-overlay" onClick={() => setShowProjectSelector(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>プロジェクトを選択</h2>

            <button
              className="create-project-button"
              onClick={() => {
                const title = prompt('プロジェクト名を入力してください:', '新規プロジェクト');
                if (title) createNewProject(title);
              }}
            >
              + 新規プロジェクト作成
            </button>

            <div className="project-list">
              {loadingProjects ? (
                <div className="loading-text">読み込み中...</div>
              ) : projects.length === 0 ? (
                <div className="empty-text">プロジェクトがありません</div>
              ) : (
                projects.map((project) => (
                  <div
                    key={project.id}
                    className="project-item"
                    onClick={() => {
                      selectProject(project.id, project.title);
                      setShowProjectSelector(false);
                    }}
                  >
                    <div className="project-item-title">{project.title || '無題'}</div>
                    <div className="project-item-date">
                      作成: {new Date(project.created_at).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              className="modal-close-button"
              onClick={() => setShowProjectSelector(false)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {showIntegratedSearch && (
        <div className="modal-overlay" onClick={() => setShowIntegratedSearch(false)}>
          <div
            className="integrated-search-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="integrated-search-header">
              <div>
                <h2>統合検索</h2>
                <span>知りたいことをそのまま質問できます</span>
              </div>
              <button
                className="integrated-search-close"
                onClick={() => setShowIntegratedSearch(false)}
                aria-label="統合検索を閉じる"
              >
                ×
              </button>
            </div>

            {integratedSearchAuthenticated === null ? (
              <div className="integrated-search-status">認証状態を確認しています...</div>
            ) : !integratedSearchAuthenticated ? (
              <div className="integrated-search-login">
                <p>統合検索を利用するには Entra ID サインインが必要です。</p>
                <button onClick={() => { window.location.href = '/login'; }}>
                  Entra ID でサインイン
                </button>
              </div>
            ) : (
              <>
                <div className="integrated-search-mode-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={integratedSearchMode === 'agent'}
                    className={integratedSearchMode === 'agent' ? 'active' : ''}
                    onClick={() => selectIntegratedSearchMode('agent')}
                    disabled={integratedSearchLoading}
                  >
                    統合検索エージェント
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={integratedSearchMode === 'search'}
                    className={integratedSearchMode === 'search' ? 'active' : ''}
                    onClick={() => selectIntegratedSearchMode('search')}
                    disabled={integratedSearchLoading}
                  >
                    検索Tool確認
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={integratedSearchMode === 'tools'}
                    className={integratedSearchMode === 'tools' ? 'active' : ''}
                    onClick={() => selectIntegratedSearchMode('tools')}
                    disabled={integratedSearchLoading}
                  >
                    MCP Tool確認
                  </button>
                </div>
                {integratedSearchMode === 'search' && (
                  <div className="integrated-search-source-picker">
                    <label htmlFor="integrated-search-source">検索先</label>
                    <select
                      id="integrated-search-source"
                      value={integratedSearchSource}
                      onChange={(event) => selectIntegratedSearchSource(event.target.value)}
                      disabled={integratedSearchLoading || integratedSearchToolsLoading}
                    >
                      {integratedSearchToolsLoading && <option value="">取得中...</option>}
                      {integratedSearchSources.map((source) => (
                        <option
                          key={source.value}
                          value={source.value}
                          disabled={!source.available}
                        >
                          {source.label}{source.available ? '' : '（利用不可）'}
                        </option>
                      ))}
                    </select>
                    {integratedSearchSources.find(
                      (source) => source.value === integratedSearchSource
                    )?.reason && (
                        <span className="integrated-search-source-reason">
                          {integratedSearchSources.find(
                            (source) => source.value === integratedSearchSource
                          ).reason}
                        </span>
                      )}
                  </div>
                )}
                {integratedSearchMode === 'search' && integratedSearchSource === 'teams' && (
                  <div className="integrated-search-teams-browser">
                    <label htmlFor="integrated-search-team">チーム</label>
                    <select
                      id="integrated-search-team"
                      value={integratedSearchTeamId}
                      onChange={(event) => selectIntegratedSearchTeam(event.target.value)}
                      disabled={integratedSearchBrowseLoading}
                    >
                      <option value="">チームを選択</option>
                      {integratedSearchTeams.map((team, index) => {
                        const teamId = getIntegratedSearchItemId(team);
                        return (
                          <option key={teamId || index} value={teamId}>
                            {getIntegratedSearchItemLabel(team)}
                          </option>
                        );
                      })}
                    </select>
                    {!integratedSearchBrowseLoading
                      && !integratedSearchBrowseError
                      && integratedSearchTeams.length === 0 && (
                        <p className="integrated-search-browser-empty">
                          所属チームがありません
                        </p>
                      )}
                    <div className="integrated-search-channel-list">
                      {integratedSearchBrowseLoading && <span>取得中...</span>}
                      {!integratedSearchBrowseLoading && integratedSearchTeamId && (
                        integratedSearchChannels.length > 0
                          ? integratedSearchChannels.map((channel, index) => (
                            <div key={getIntegratedSearchItemId(channel) || index}>
                              {getIntegratedSearchItemLabel(channel)}
                            </div>
                          ))
                          : <span>チャネルがありません</span>
                      )}
                    </div>
                    {integratedSearchBrowseError && (
                      <p className="integrated-search-tool-error">
                        {integratedSearchBrowseError}
                      </p>
                    )}
                  </div>
                )}
                {integratedSearchMode === 'search'
                  && integratedSearchSource === 'sharepoint' && (
                    <div className="integrated-search-query-guide">
                      <strong>SharePoint キーワード検索</strong>
                      <span>
                        ファイル名や本文の語句をスペースまたは改行で区切って入力してください。
                        共有 URL を直接開く検索ではありません。
                      </span>
                    </div>
                  )}
                <div
                  ref={integratedSearchMessagesRef}
                  className="integrated-search-messages"
                >
                  {integratedSearchMessages.length === 0 && (
                    <div className="integrated-search-empty">
                      調べたい内容を入力してください。
                    </div>
                  )}
                  {integratedSearchMessages.map((message, index) => (
                    <div
                      key={`${message.role}-${index}`}
                      className={`integrated-search-message ${message.role}${message.searchSource === 'sharepoint' ? ' source-sharepoint' : ''
                        }`}
                    >
                      {(message.mode === 'tools' || message.searchSource) && (
                        <div className="integrated-search-message-meta">
                          <span>
                            {message.mode === 'tools'
                              ? getIntegratedSearchToolCategory(message.toolName || '')
                              : getIntegratedSearchSourceLabel(
                                integratedSearchSources,
                                message.searchSource
                              )}
                          </span>
                          {message.mode === 'tools' && message.toolName && (
                            <code>{message.toolName}</code>
                          )}
                        </div>
                      )}
                      {message.role === 'user' && message.mode === 'tools' ? (
                        <pre className="integrated-search-arguments">
                          <code>{message.content}</code>
                        </pre>
                      ) : message.role === 'user' ? (
                        message.content
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          components={integratedSearchMarkdownComponents}
                          urlTransform={sanitizeIntegratedSearchUrl}
                        >
                          {message.content}
                        </ReactMarkdown>
                      )}
                    </div>
                  ))}
                  {integratedSearchLoading && (
                    <div className="integrated-search-message assistant loading">
                      検索しています...
                    </div>
                  )}
                </div>
                {integratedSearchMode === 'tools' && (
                  <div className="integrated-search-tool-picker">
                    <label htmlFor="integrated-search-tool">MCP Tool</label>
                    <select
                      id="integrated-search-tool"
                      value={integratedSearchToolName}
                      onChange={(event) => selectIntegratedSearchTool(event.target.value)}
                      disabled={integratedSearchLoading || integratedSearchToolsLoading}
                    >
                      {integratedSearchToolsLoading && <option value="">取得中...</option>}
                      {!integratedSearchToolsLoading && integratedSearchTools.length === 0 && (
                        <option value="">利用できる Tool がありません</option>
                      )}
                      {integratedSearchTools.map((tool) => (
                        <option key={tool.name} value={tool.name}>
                          [{getIntegratedSearchToolCategory(tool.name)}] {tool.name}
                        </option>
                      ))}
                    </select>
                    {integratedSearchToolsError && (
                      <p className="integrated-search-tool-error">
                        {integratedSearchToolsError}
                      </p>
                    )}
                    {selectedIntegratedSearchTool?.description && (
                      <p>{selectedIntegratedSearchTool.description}</p>
                    )}
                    {selectedIntegratedSearchTool && (
                      <details>
                        <summary>Input schema</summary>
                        <pre>{JSON.stringify(selectedIntegratedSearchTool.input_schema, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                )}
                {!(integratedSearchMode === 'search' && integratedSearchSource === 'teams') && (
                  <div className="integrated-search-composer">
                    <textarea
                      className={integratedSearchMode === 'tools' ? 'json-input' : ''}
                      value={integratedSearchMode === 'tools'
                        ? integratedSearchToolArguments
                        : integratedSearchInput}
                      onChange={(event) => {
                        if (integratedSearchMode === 'tools') {
                          setIntegratedSearchToolArguments(event.target.value);
                        } else {
                          setIntegratedSearchInput(event.target.value);
                        }
                      }}
                      onKeyDown={(event) => {
                        const shouldSend = integratedSearchMode === 'tools'
                          ? event.key === 'Enter' && (event.ctrlKey || event.metaKey)
                          : event.key === 'Enter' && !event.shiftKey;
                        if (shouldSend) {
                          event.preventDefault();
                          sendIntegratedSearch();
                        }
                      }}
                      placeholder={integratedSearchMode === 'tools'
                        ? '{\n  "query": "調べたい内容"\n}'
                        : integratedSearchSource === 'sharepoint'
                          ? '例: 売上実績 2026 上期\nまたは、キーワードを1行ずつ入力'
                          : '質問を入力'}
                      aria-label={integratedSearchMode === 'tools'
                        ? 'MCP Tool の JSON 引数'
                        : '統合検索への質問'}
                      rows={integratedSearchMode === 'tools' ? 5 : 2}
                      disabled={integratedSearchLoading}
                    />
                    <button
                      onClick={sendIntegratedSearch}
                      disabled={
                        (integratedSearchMode === 'tools' && !integratedSearchToolName)
                        || !(integratedSearchMode === 'tools'
                          ? integratedSearchToolArguments
                          : integratedSearchInput).trim()
                        || integratedSearchLoading
                      }
                    >
                      {integratedSearchMode === 'tools' ? '実行' : '送信'}
                    </button>
                  </div>
                )}
                <div className="integrated-search-disclaimer">
                  AI の回答なので100%正しいとは限りません。
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* メインコンテンツエリア（左右分割） */}
      <div className="main-content">
        {/* 左サイドバー - 入力ファイル一覧 */}
        <div className="file-sidebar">
          <div className="file-sidebar-header">
            <h3>入力ファイル</h3>
            <label className="file-upload-button">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                disabled={uploadingFiles}
                style={{ display: 'none' }}
              />
              <span>{uploadingFiles ? '⏳' : '📁+'}</span>
            </label>
          </div>
          <div className="file-list">
            {projectFiles.length === 0 ? (
              <div className="file-list-empty">
                ファイルをアップロードしてください
              </div>
            ) : (
              projectFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="file-item"
                  title={`クリックでコピー: ${file.name}`}
                  onClick={() => handleFileNameClick(file.name)}
                >
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file.name}</span>
                </div>
              ))
            )}
          </div>

          {/* 現在のLLM認識内容パネル（Step1/Step2のみ表示） */}
          {(activeStep.id === 'step1' || activeStep.id === 'step2') && (
            <div className="current-req-panel">
              <div
                className="current-req-header"
                onClick={() => setCurrentReqExpanded(p => !p)}
                title="クリックで展開/折りたたみ"
              >
                <span>📝 現在の認識内容</span>
                <span className="current-req-toggle">{currentReqExpanded ? '▲' : '▼'}</span>
              </div>
              {currentReqExpanded && (
                <div className="current-req-body">
                  {currentRequirementsByStep[activeStep.id] ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {stripMarkdownCodeBlock(currentRequirementsByStep[activeStep.id])}
                    </ReactMarkdown>
                  ) : (
                    <div className="current-req-empty">
                      まだ内容がありません<br />会話を始めると更新されます
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右メインエリア */}
        <div className="main-area">
          <div className="tabs-container">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                onClick={() => setActiveTab(idx)}
                className={`tab ${activeTab === idx ? 'active' : ''}`}
              >
                {step.label}
              </div>
            ))}
          </div>

          <div className="step-description">{activeStep.description}</div>

          {/* 設定パネル（ポップアップメニュー） */}
          {showSettings && (
            <div ref={settingsRef} className="settings-popup">
              {/* LLM設定セクション */}
              <div className="section">
                <div className="section-title">LLM設定（モデル・トークン・温度）</div>

                {/* 一括設定 */}
                <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '6px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    一括設定
                  </label>

                  {/* モデル選択 */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>モデル</label>
                    <select
                      value={bulkConfig.deployment}
                      onChange={(e) => setBulkConfig({ ...bulkConfig, deployment: e.target.value })}
                      disabled={loadingModels || loading}
                      className="model-selector"
                      style={{ width: '100%' }}
                    >
                      {loadingModels ? (
                        <option>読み込み中...</option>
                      ) : availableModels.length === 0 ? (
                        <option>デプロイメントが利用できません</option>
                      ) : (
                        availableModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} ({model.provider})
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Max Tokens スライダー */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                      Max Tokens: {bulkConfig.max_tokens_percent}%
                      {bulkConfig.deployment && availableModels.find(m => m.id === bulkConfig.deployment) && (
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {' '}(= {Math.floor(availableModels.find(m => m.id === bulkConfig.deployment).max_tokens * bulkConfig.max_tokens_percent / 100)} tokens)
                        </span>
                      )}
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={bulkConfig.max_tokens_percent}
                      onChange={(e) => setBulkConfig({ ...bulkConfig, max_tokens_percent: parseInt(e.target.value) })}
                      style={{ width: '100%' }}
                      title="精度と応答速度のバランス調整（低い=速い/安価、高い=詳細/高コスト）"
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      💡 低い値: 応答速度優先、高い値: 詳細な分析
                    </div>
                  </div>

                  {/* Temperature スライダー */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                      Temperature: {bulkConfig.temperature.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={bulkConfig.temperature}
                      onChange={(e) => setBulkConfig({ ...bulkConfig, temperature: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                      title="出力の創造性（0.0=決定的、1.0=創造的）"
                    />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      💡 0.0=安定した出力、1.0=多様な出力
                    </div>
                  </div>

                  <button
                    onClick={applyBulkConfig}
                    disabled={loadingModels || loading || !bulkConfig.deployment}
                    style={{ padding: '8px 16px', width: '100%' }}
                    title="選択した設定を全Stepに適用"
                  >
                    全Stepに適用
                  </button>
                </div>

                {/* Step毎の個別設定 */}
                <div style={{ marginBottom: '16px' }}>
                  {steps.map((step) => {
                    const config = llmConfigByStep[step.id];
                    const modelInfo = availableModels.find(m => m.id === config.deployment);
                    return (
                      <div key={step.id} style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                          {step.label}
                        </label>

                        {/* モデル選択 */}
                        <select
                          value={config.deployment}
                          onChange={(e) => setLlmConfigByStep({
                            ...llmConfigByStep,
                            [step.id]: { ...config, deployment: e.target.value }
                          })}
                          disabled={loadingModels || loading}
                          className="model-selector"
                          style={{ width: '100%', marginBottom: '8px' }}
                        >
                          {loadingModels ? (
                            <option>読み込み中...</option>
                          ) : availableModels.length === 0 ? (
                            <option>デプロイメントが利用できません</option>
                          ) : (
                            availableModels.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name} ({model.provider})
                              </option>
                            ))
                          )}
                        </select>

                        {/* Max Tokens */}
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          Max Tokens: {config.max_tokens_percent}%
                          {modelInfo && (
                            <span style={{ color: '#666' }}>
                              {' '}(= {Math.floor(modelInfo.max_tokens * config.max_tokens_percent / 100)} tokens)
                            </span>
                          )}
                        </div>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          step="5"
                          value={config.max_tokens_percent}
                          onChange={(e) => setLlmConfigByStep({
                            ...llmConfigByStep,
                            [step.id]: { ...config, max_tokens_percent: parseInt(e.target.value) }
                          })}
                          style={{ width: '100%', marginBottom: '8px' }}
                        />

                        {/* Temperature */}
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          Temperature: {config.temperature.toFixed(1)}
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={config.temperature}
                          onChange={(e) => setLlmConfigByStep({
                            ...llmConfigByStep,
                            [step.id]: { ...config, temperature: parseFloat(e.target.value) }
                          })}
                          style={{ width: '100%' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {renderNewTrialButton()}

          {renderArtifactSelector()}

          <div ref={chatRef} className="chat-area">
            {messages.map((m, i) => (
              <div key={i} className={`message-container ${m.role}`}>
                <div className={`message-bubble ${m.role}`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{m.content}</ReactMarkdown>
                  {m.artifact && (
                    <div className="artifact-link-container">
                      <span
                        className="artifact-link"
                        onClick={() => handleArtifactLinkClick(m.artifact)}
                        onMouseEnter={() => {
                          handleArtifactEnter();
                          handleArtifactHover(m.artifact);
                        }}
                        onMouseLeave={handleArtifactLeave}
                      >
                        📦 成果物: {m.artifact.title}
                      </span>
                    </div>
                  )}
                  {m.artifacts && m.artifacts.length > 0 && (
                    <div className="artifact-link-container">
                      {m.artifacts.map((art, idx) => (
                        <span
                          key={idx}
                          className="artifact-link"
                          onClick={() => handleArtifactLinkClick(art)}
                          onMouseEnter={() => {
                            handleArtifactEnter();
                            handleArtifactHover(art);
                          }}
                          onMouseLeave={handleArtifactLeave}
                        >
                          📦 成果物 {idx + 1}: {art.title}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.referencedFiles && m.referencedFiles.length > 0 && (
                    <div className="referenced-files-container">
                      <span className="referenced-files-label">🔍 参照ファイル:</span>
                      {m.referencedFiles.map((f, idx) => (
                        <span key={idx} className="referenced-file-chip">{f}</span>
                      ))}
                    </div>
                  )}
                  {m.nodeTrace && m.nodeTrace.length > 0 && (
                    <div className="routing-info">
                      <button
                        className="routing-toggle"
                        onClick={() => {
                          const key = `${activeStep.id}-${i}`;
                          setExpandedRoutings(prev => ({ ...prev, [key]: !prev[key] }));
                        }}
                      >
                        {expandedRoutings[`${activeStep.id}-${i}`] ? '▼' : '▶'} Agentic の詳細
                      </button>
                      {expandedRoutings[`${activeStep.id}-${i}`] && (
                        <div className="routing-detail">
                          <div className="node-trace">
                            {m.nodeTrace.map((entry, traceIdx) => (
                              <React.Fragment key={traceIdx}>
                                {traceIdx > 0 && <span className="node-trace-arrow">→</span>}
                                <span className={`node-trace-label node-trace-${entry.node}`}>
                                  {({ revise: '📝 要件更新', key_question: '❓ 重点の特定', think: '💭 LLM 推論', read_file: '📄 ファイル読込', search: '🔍 RAG 検索', proposal: '📋 提案の整形', ask_user: '💬 確認' })[entry.node] || entry.node}
                                  {entry.node === 'search' && entry.referenced_files && (
                                    <span className="node-trace-count"> ({entry.referenced_files.length} ファイル)</span>
                                  )}
                                  {entry.node === 'search' && entry.search_status === 'no_results' && !entry.referenced_files && (
                                    <span className="node-trace-count"> (結果なし)</span>
                                  )}
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Q&A カード: メッセージ内にインライン配置 */}
                  {!loading && m.role === 'assistant' && i === messages.length - 1
                    && qaFieldsByStep[activeStep.id] && qaFieldsByStep[activeStep.id].length > 0
                    && (interruptType === 'ask_user' || interruptType === 'proposal' || interruptType === 'prepare_batch_qa')
                    && (() => {
                      const sid = activeStep.id;
                      const fields = qaFieldsByStep[sid];
                      const currentIdx = qaIndexByStep[sid] || 0;
                      const qa = fields[currentIdx];
                      const answers = qaAnswersByStep[sid] || {};
                      const currentAnswer = answers[qa.field_key];
                      const freeInput = qaFreeInputByStep[sid] || '';
                      const answeredCount = fields.filter(f =>
                        f.status === 'user_provided' || answers[f.field_key]
                      ).length;
                      const allAnswered = answeredCount === fields.length;
                      const isSuggestionSelected = currentAnswer && qa.suggestions?.includes(currentAnswer);

                      return (
                        <div className="qa-card-inline">
                          <div className="qa-batch-card">
                            {/* ヘッダー: ← タイトル → */}
                            <div className="qa-batch-header">
                              <button
                                className="qa-nav-btn"
                                onClick={() => handleQaNavigate(sid, -1)}
                                disabled={currentIdx === 0}
                              >
                                ←
                              </button>
                              <div className="qa-batch-title">
                                <span className="qa-batch-progress">Q{currentIdx + 1}/{fields.length}</span>
                                <span className="qa-batch-label">{qa.label}</span>
                                <span className="qa-batch-category">{qa.category}</span>
                              </div>
                              <button
                                className="qa-nav-btn"
                                onClick={() => handleQaNavigate(sid, 1)}
                                disabled={currentIdx === fields.length - 1}
                              >
                                →
                              </button>
                            </div>

                            {/* 進捗ドット（クリックで遷移） */}
                            <div className="qa-progress-dots">
                              {fields.map((f, idx) => (
                                <span
                                  key={f.field_key}
                                  className={`qa-dot ${idx === currentIdx ? 'active' : ''} ${f.status === 'user_provided' || answers[f.field_key] ? 'answered' : ''} ${answers[f.field_key] === '__SKIP__' ? 'skipped' : ''}`}
                                  title={`${idx + 1}. ${f.label}`}
                                  onClick={() => {
                                    setQaIndexByStep(prev => ({ ...prev, [sid]: idx }));
                                    setQaFreeInputByStep(prev => ({ ...prev, [sid]: '' }));
                                  }}
                                />
                              ))}
                            </div>

                            {/* 説明 */}
                            {qa.description && (
                              <p className="qa-batch-description">{qa.description}</p>
                            )}

                            {/* スキップ済み表示 */}
                            {currentAnswer === '__SKIP__' && (
                              <div className="qa-current-answer skipped">
                                <span className="qa-current-answer-label">スキップ済み</span>
                                <button className="qa-clear-btn" onClick={() => handleQaClearAnswer(sid, qa.field_key)}>✕</button>
                              </div>
                            )}

                            {/* 自由入力の回答表示（選択肢にないカスタム回答の場合のみ） */}
                            {currentAnswer && currentAnswer !== '__SKIP__' && !isSuggestionSelected && (
                              <div className="qa-current-answer">
                                <span className="qa-current-answer-label">入力済み:</span>
                                <span className="qa-current-answer-text">{currentAnswer}</span>
                                <button className="qa-clear-btn" onClick={() => handleQaClearAnswer(sid, qa.field_key)}>✕</button>
                              </div>
                            )}

                            {/* 候補ボタン（常に表示、選択状態はハイライト） */}
                            {currentAnswer !== '__SKIP__' && qa.suggestions && qa.suggestions.length > 0 && (
                              <div className="qa-batch-suggestions">
                                {qa.suggestions.map((suggestion, sIdx) => (
                                  <button
                                    key={sIdx}
                                    className={`qa-suggestion-btn ${currentAnswer === suggestion ? 'selected' : ''}`}
                                    onClick={() => handleQaSelectSuggestion(sid, qa.field_key, suggestion)}
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* 自由入力（スキップ済み以外は常に表示） */}
                            {currentAnswer !== '__SKIP__' && (
                              <div className="qa-freeform">
                                <input
                                  className="qa-freeform-input"
                                  placeholder="自分で入力する..."
                                  value={freeInput}
                                  onChange={e => setQaFreeInputByStep(prev => ({ ...prev, [sid]: e.target.value }))}
                                  onKeyDown={e => { if (e.key === 'Enter') handleQaFreeSubmit(sid, qa.field_key); }}
                                />
                                <button
                                  className="qa-freeform-submit"
                                  onClick={() => handleQaFreeSubmit(sid, qa.field_key)}
                                  disabled={!freeInput.trim()}
                                >
                                  決定
                                </button>
                              </div>
                            )}

                            {/* スキップボタン（未回答時のみ） */}
                            {!currentAnswer && (
                              <button
                                className="qa-batch-skip-btn"
                                onClick={() => handleQaFieldSkip(sid, qa.field_key)}
                              >
                                ⏭ スキップ
                              </button>
                            )}

                            {/* 一括送信ボタン（全項目回答後のみ有効） */}
                            <div className="qa-batch-footer">
                              <span className="qa-batch-status">{answeredCount}/{fields.length} 回答済み</span>
                              <button
                                className="qa-batch-submit-btn"
                                onClick={() => handleQaBatchSubmit(sid)}
                                disabled={!allAnswered}
                              >
                                ✅ 回答を送信
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message-container bot">
                <div className="message-bubble bot loading">
                  <span className="typing-indicator">
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                    <span className="typing-dot"></span>
                  </span>
                  <span className="typing-label">エージェントが考え中...</span>
                </div>
              </div>
            )}
          </div>

          {/* 共通プレビューツールチップ（ドロップダウンとチャット両方で使用） */}
          {hoveredArtifact && (
            <div
              className="preview-tooltip"
              onClick={handlePreviewClick}
              onMouseEnter={handleArtifactEnter}
              onMouseLeave={handleArtifactLeave}
            >
              <div className="preview-content">
                {loadingContent ? (
                  <div className="preview-loading">読み込み中...</div>
                ) : hoveredContentType === 'text/html' ? (
                  <iframe
                    srcDoc={hoveredContent || '<p>内容がありません</p>'}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="HTMLプレビュー"
                  />
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                    {stripMarkdownCodeBlock(hoveredContent || hoveredArtifact.summary || '内容がありません')}
                  </ReactMarkdown>
                )}
              </div>
              <div className="preview-hint">クリックで全画面表示</div>
            </div>
          )}

          {/* モーダルダイアログ（全画面表示） */}
          <dialog ref={dialogRef} className="artifact-dialog">
            <div className="dialog-header">
              <h2>{fullscreenArtifact?.title || '成果物'}</h2>
              <button onClick={handleCloseDialog} className="dialog-close-button">
                ✕
              </button>
            </div>
            <div className="dialog-content">
              {fullscreenContentType === 'text/html' ? (
                <iframe
                  srcDoc={fullscreenContent || '<p>内容がありません</p>'}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="HTMLプレビュー（全画面）"
                />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {stripMarkdownCodeBlock(fullscreenContent || fullscreenArtifact?.summary || '内容がありません')}
                </ReactMarkdown>
              )}
            </div>
          </dialog>

        </div>{/* main-area 閉じタグ */}

      </div>{/* main-content 閉じタグ */}

      <div className="input-area">
        {activeStep.id === 'step1' && (interruptType === 'ask_user' || interruptType === 'proposal' || interruptType === 'prepare_batch_qa') && !loading && (
          <button
            onClick={handleForceComplete}
            className="proceed-button"
            title="現在の要件定義内容で次のステップへ進みます"
          >
            ✅ このまま次へ進む
          </button>
        )}
        <input
          value={input}
          onChange={e => setInputsByStep(prev => ({ ...prev, [activeStep.id]: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
          disabled={loading}
        />
        <button onClick={() => sendMessage()} disabled={loading || !input} className="send-button">
          Send
        </button>
      </div>
    </div>
  );
}

// UUID形式の妥当性検証ヘルパー関数（型安全性向上）
function isValidUUID(str) {
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return typeof str === 'string' && uuidPattern.test(str);
}

export default App;
